import React, { useEffect, useState } from "react";
import { PageLoader, StatusBadge } from "../../components/shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { money, apiErr } from "../../lib/api";

export default function AdminCatalog() {
  const [genres, setGenres] = useState([]);
  const [cities, setCities] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ng, setNg] = useState("");
  const [nc, setNc] = useState("");
  const [ncoupon, setNcoupon] = useState({ code: "", type: "percent", value: 10, min_amount: 0, max_discount: "" });

  const load = () => Promise.all([api.get("/admin/genres"), api.get("/admin/cities"), api.get("/admin/coupons")])
    .then(([g, c, cp]) => { setGenres(g.data.genres); setCities(c.data.cities); setCoupons(cp.data.coupons); }).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const addGenre = async () => { if (!ng) return; await api.post("/admin/genres", { name: ng }); setNg(""); toast.success("Genre added."); load(); };
  const toggleGenre = async (g) => { await api.put(`/admin/genres/${g.id}`, { enabled: !g.enabled }); load(); };
  const delGenre = async (id) => { await api.delete(`/admin/genres/${id}`); load(); };
  const addCity = async () => { if (!nc) return; await api.post("/admin/cities", { name: nc }); setNc(""); toast.success("City added."); load(); };
  const delCity = async (id) => { await api.delete(`/admin/cities/${id}`); load(); };
  const addCoupon = async () => {
    try { await api.post("/admin/coupons", { ...ncoupon, max_discount: ncoupon.max_discount || null }); toast.success("Coupon created."); setNcoupon({ code: "", type: "percent", value: 10, min_amount: 0, max_discount: "" }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (loading) return <PageLoader />;

  return (
    <div>
      <div className="overline mb-1">Configuration</div>
      <h1 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight mb-6">Genres, Cities & Coupons</h1>
      <Tabs defaultValue="genres">
        <TabsList className="mb-6"><TabsTrigger value="genres">Genres</TabsTrigger><TabsTrigger value="cities">Cities</TabsTrigger><TabsTrigger value="coupons">Coupons</TabsTrigger></TabsList>

        <TabsContent value="genres">
          <div className="flex gap-2 mb-4 max-w-sm"><Input value={ng} onChange={(e) => setNg(e.target.value)} placeholder="New genre" data-testid="new-genre" /><Button onClick={addGenre} className="rounded-full"><Plus /></Button></div>
          <div className="rounded-xl border border-border bg-card divide-y divide-border">
            {genres.map((g) => (
              <div key={g.id} className="flex items-center justify-between p-3" data-testid={`genre-${g.id}`}>
                <span className="font-medium" style={{ borderLeft: `3px solid ${g.color}`, paddingLeft: 8 }}>{g.name}</span>
                <div className="flex items-center gap-3"><Switch checked={g.enabled} onCheckedChange={() => toggleGenre(g)} /><button onClick={() => delGenre(g.id)} className="text-destructive"><Trash size={16} /></button></div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="cities">
          <div className="flex gap-2 mb-4 max-w-sm"><Input value={nc} onChange={(e) => setNc(e.target.value)} placeholder="New city" data-testid="new-city" /><Button onClick={addCity} className="rounded-full"><Plus /></Button></div>
          <div className="grid sm:grid-cols-3 gap-2">
            {cities.map((c) => (
              <div key={c.id} className="flex items-center justify-between p-3 rounded-lg border border-border bg-card"><span>{c.name}</span><button onClick={() => delCity(c.id)} className="text-destructive"><Trash size={15} /></button></div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="coupons">
          <div className="rounded-xl border border-border bg-card p-4 mb-4 grid sm:grid-cols-5 gap-2 items-end">
            <div><Label className="text-xs">Code</Label><Input value={ncoupon.code} onChange={(e) => setNcoupon({ ...ncoupon, code: e.target.value.toUpperCase() })} data-testid="coupon-code" /></div>
            <div><Label className="text-xs">Type</Label><Select value={ncoupon.type} onValueChange={(v) => setNcoupon({ ...ncoupon, type: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="percent">Percent</SelectItem><SelectItem value="flat">Flat</SelectItem></SelectContent></Select></div>
            <div><Label className="text-xs">Value</Label><Input type="number" value={ncoupon.value} onChange={(e) => setNcoupon({ ...ncoupon, value: +e.target.value })} /></div>
            <div><Label className="text-xs">Min ₹</Label><Input type="number" value={ncoupon.min_amount} onChange={(e) => setNcoupon({ ...ncoupon, min_amount: +e.target.value })} /></div>
            <Button onClick={addCoupon} className="rounded-full" data-testid="add-coupon"><Plus className="mr-1" /> Add</Button>
          </div>
          <div className="rounded-xl border border-border bg-card divide-y divide-border">
            {coupons.map((c) => (
              <div key={c.id} className="flex items-center justify-between p-3" data-testid={`coupon-${c.id}`}>
                <div><span className="font-mono font-semibold">{c.code}</span> <span className="text-sm text-muted-foreground ml-2">{c.type === "percent" ? `${c.value}% off` : money(c.value)} · min {money(c.min_amount)}</span></div>
                <StatusBadge status={c.active ? "active" : "unpublished"} />
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
